import type {FormDefinitionOfflineBundleResponse} from 'src/api/ProactApiClient';
import type {HydrationPlanEntry} from 'src/offline/offlineSessionManager';
import {getStateMachine} from './api';
import {
  formDefinitionKey,
  formDefinitionsListKey,
  formsAssetsKey,
  formsPersonnelKey,
  formStateMachineKey,
} from './snapshotKeys';

// A definition's JSON is small; this over-estimates for the pre-cache quota
// guard so a large tenant catalog can't silently fill the origin's quota.
const ESTIMATED_DEFINITION_BYTES = 100_000;
const ESTIMATED_PERSONNEL_ROW_BYTES = 100;
const ESTIMATED_ASSET_ROW_BYTES = 300;

export const estimateBundleBytes = (
  bundle: FormDefinitionOfflineBundleResponse
): number =>
  bundle.definitions.length * ESTIMATED_DEFINITION_BYTES +
  (bundle.personnel?.rows.length ?? 0) * ESTIMATED_PERSONNEL_ROW_BYTES +
  (bundle.assets?.rows.length ?? 0) * ESTIMATED_ASSET_ROW_BYTES;

// Snapshot plan for the forms session: the navigation list plus one definition
// per id, stored in the flattenSubform:true shape the fill UI reads.
export const buildFormsHydrationPlan = (
  bundle: FormDefinitionOfflineBundleResponse
): HydrationPlanEntry[] => {
  const personnel = bundle.personnel;
  const assets = bundle.assets;
  return [
    {key: formDefinitionsListKey, run: async () => bundle.definitions},
    // The fill Save bar reads this static-per-instance machine; cache it so the
    // bar renders offline for a user who never opened a fill page online.
    {key: formStateMachineKey, run: () => getStateMachine()},
    ...bundle.definitions.map(definition => ({
      key: formDefinitionKey(definition.id),
      run: async () => definition,
    })),
    // Reference sections are absent when no definition uses the field type;
    // rehydration then drops any stale row left by an earlier capture.
    ...(personnel
      ? [{key: formsPersonnelKey, run: async () => personnel}]
      : []),
    ...(assets ? [{key: formsAssetsKey, run: async () => assets}] : []),
  ];
};
